import { Loader } from "@googlemaps/js-api-loader";
import { Card, Col, Icon, notification, Row, Spin, Typography } from "antd";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as turf from "turf";
import * as Yup from "yup";
import { useMapManager } from "./hooks/useMapManager";

// Components
import ButtonStandard from "../../../../components/utils/button";
import PastureStatusDashboard from "../pastureStatus";
import AreaForm from "./components/AreaForm";
import AreaInfoModal from "./components/AreaInfoModal";
import MapCenterCard from "./components/MapCenterCard";

// Services
import {
  deleteFarmMap,
  getFarmMaps,
  saveFarmMap,
  updateFarmMap,
} from "../../../../services/mapService";
import { logEventError, logEventInfo } from "../../../../utils/logger";

// Config
import {
  BREAK_ICON,
  BULL_ICON,
  MAINTENANCE_ICON,
  MAP_CONFIG,
  POLYGON_STYLES,
  RENOVATION_ICON,
} from "./config/constants";

// Styles
import { numberMask } from "../../../../services/helpersMethodsService";
import { getListOfProfitCenterIndex } from "../../../../services/profitCenterService";
import AreaMapProvider from "./context";
import {
  DrawingOverlay,
  FormCard,
  MapContainer,
  MapDrawWrapper,
} from "./styles";

const getValidationSchema = (translation) =>
  Yup.object({
    identificationCode: Yup.string().required(
      translation.areaMap.validation.identificationCodeRequired,
    ),
    type: Yup.string().required(translation.areaMap.validation.typeRequired),
    picketId: Yup.string().nullable(),
    retreatId: Yup.string().nullable(),
    observation: Yup.string().nullable(),
    color: Yup.string().required(translation.areaMap.validation.colorRequired),
  });

const initialValues = {
  picketId: null,
  retreatId: null,
  observation: "",
  color: "#FF0000",
  area: 0,
  areaInSquareMeters: 0,
  areaInHectares: 0,
};

const AreaMapDashboardLayout = () => {
  const [shouldShowDashboardsExtensive, setShouldShowDashboardsExtensive] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [existingPolygons, setExistingPolygons] = useState(new Map());

  const [formValues, setFormValues] = useState(initialValues);
  const [area, setArea] = useState(0);
  const [mapCenter, setMapCenter] = useState(MAP_CONFIG.DEFAULT_CENTER);
  const [savedLocation, setSavedLocation] = useState(null);
  const [totalAreas, setTotalAreas] = useState(0);

  // Estado do overlay de ajuda exibido sobre o mapa durante o desenho
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  const [vertexCount, setVertexCount] = useState(0);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const handlePolygonClickRef = useRef(null);
  const currentColorRef = useRef(formValues.color);

  // Desenho customizado de polígonos (substitui o DrawingManager, removido na
  // Maps JS API v3.65). O usuário clica no mapa para adicionar vértices e dá
  // duplo-clique para concluir o polígono.
  const isDrawingRef = useRef(false);
  const drawingPointsRef = useRef([]);
  const tempPolylineRef = useRef(null);
  const drawingListenersRef = useRef([]);
  const existingPolygonsRef = useRef(existingPolygons);

  const {
    setModalVisible,
    setSelectedAreaData,
    setIsEditMode,
    setEditingAreaData,
    isEditMode,
    editingAreaData,
  } = useMapManager();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId, confinement },
  } = useSelector((state) => state.app);

  const fetchActivities = useCallback(async () => {
    try {
      const {
        data: { results: listActivities },
      } = await getListOfProfitCenterIndex({
        groupId,
        farmId,
      });
      let enableDashboardsExtensive = true;
      let enableDashboardsIntensive = true;
      if (
        listActivities.filter(
          (activity) =>
            activity.productionSubSystem === "Extensive" ||
            activity.productionSubSystem === "SemiIntensive",
        ).length > 0
      ) {
        enableDashboardsExtensive = true;
      } else {
        enableDashboardsExtensive = false;
      }

      if (
        listActivities.filter(
          (activity) => activity.productionSubSystem === "Intensive",
        ).length > 0 &&
        confinement
      ) {
        enableDashboardsIntensive = true;
      } else {
        enableDashboardsIntensive = false;
      }
      setShouldShowDashboardsExtensive(enableDashboardsExtensive);
    } catch (error) {}
  }, [groupId, farmId, confinement]);

  // Encerra o modo de desenho: remove listeners, polyline temporária e
  // restaura a interatividade dos polígonos existentes.
  const disableDrawing = useCallback(() => {
    isDrawingRef.current = false;

    // Remover listeners de desenho do mapa
    drawingListenersRef.current.forEach((listener) => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    });
    drawingListenersRef.current = [];

    // Remover a polyline de pré-visualização e zerar os pontos
    if (tempPolylineRef.current) {
      tempPolylineRef.current.setMap(null);
      tempPolylineRef.current = null;
    }
    drawingPointsRef.current = [];

    // Restaurar cursor padrão e a clicabilidade dos polígonos existentes
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ draggableCursor: null });
    }
    existingPolygonsRef.current.forEach((item) => {
      if (item && typeof item.setOptions === "function") {
        item.setOptions({ clickable: true });
      }
    });

    // Ocultar o overlay de ajuda
    setIsDrawingActive(false);
    setVertexCount(0);
  }, []);

  // Conclui o desenho: cria o polígono final a partir dos vértices clicados e
  // entrega para o fluxo existente (cálculo de área, edição, etc.).
  const finishDrawing = useCallback(() => {
    if (!isDrawingRef.current) return;

    // Remover vértices finais duplicados (o duplo-clique de conclusão pode
    // registrar o último ponto mais de uma vez).
    const points = drawingPointsRef.current.slice();
    while (points.length >= 2) {
      const last = points[points.length - 1];
      const prev = points[points.length - 2];
      if (
        Math.abs(last.lat() - prev.lat()) < 1e-9 &&
        Math.abs(last.lng() - prev.lng()) < 1e-9
      ) {
        points.pop();
      } else {
        break;
      }
    }

    // É preciso ao menos 3 vértices para formar um polígono
    if (points.length < 3) {
      return;
    }

    const color = currentColorRef.current;
    const polygon = new window.google.maps.Polygon({
      paths: points,
      fillColor: color,
      strokeColor: color,
      ...POLYGON_STYLES,
    });
    polygon.setMap(mapInstanceRef.current);

    // Encerrar o modo de desenho (limpa polyline/listeners)
    disableDrawing();

    // Se já existia um polígono em rascunho, removê-lo
    if (drawnPolygon) {
      drawnPolygon.setMap(null);
    }

    setDrawnPolygon(polygon);
    calculateArea(polygon);
    addPolygonEditListeners(polygon);
  }, [disableDrawing, drawnPolygon, calculateArea, addPolygonEditListeners]);

  // Inicia o modo de desenho customizado de polígono.
  const enableDrawing = useCallback(
    (color = currentColorRef.current) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      // Garantir que não há um desenho anterior em andamento
      disableDrawing();
      isDrawingRef.current = true;
      drawingPointsRef.current = [];
      // Exibir o overlay de ajuda sobre o mapa
      setIsDrawingActive(true);
      setVertexCount(0);

      // Evitar que cliques em polígonos existentes sejam capturados por eles
      // (permite adicionar vértices sobre áreas já desenhadas).
      existingPolygonsRef.current.forEach((item) => {
        if (item && typeof item.setOptions === "function") {
          item.setOptions({ clickable: false });
        }
      });

      // Cursor em forma de cruz indicando modo de desenho
      map.setOptions({ draggableCursor: "crosshair" });

      // Polyline temporária para pré-visualizar o traçado
      tempPolylineRef.current = new window.google.maps.Polyline({
        path: [],
        strokeColor: color,
        strokeOpacity: 1,
        strokeWeight: 2,
        clickable: false,
        map,
      });

      // Cada clique adiciona um vértice
      const clickListener = map.addListener("click", (event) => {
        drawingPointsRef.current.push(event.latLng);
        if (tempPolylineRef.current) {
          tempPolylineRef.current.setPath(drawingPointsRef.current);
        }
        setVertexCount(drawingPointsRef.current.length);
      });

      // Duplo-clique conclui o polígono
      const dblClickListener = map.addListener("dblclick", () => {
        finishDrawing();
      });

      drawingListenersRef.current = [clickListener, dblClickListener];
    },
    [disableDrawing, finishDrawing],
  );

  const clearExistingPolygons = useCallback(() => {
    setExistingPolygons((prev) => {
      prev.forEach((item) => {
        if (!item) return;
        // Polígonos usam setMap; AdvancedMarkerElement usa a propriedade map.
        if (typeof item.setMap === "function") {
          item.setMap(null);
        } else {
          item.map = null;
        }
      });
      return new Map();
    });
  }, []);

  const loadExistingAreas = useCallback(async () => {
    try {
      setIsLoading(true);

      // Adicionar timestamp para evitar cache
      const response = await getFarmMaps({
        groupId,
        farmId,
      });
      const areas = response.data?.results || [];

      clearExistingPolygons();

      // Renderizar áreas existentes no mapa
      if (mapInstanceRef.current && areas.length > 0) {
        const newPolygonsMap = new Map();
        setTotalAreas(
          areas.reduce((acc, area) => acc + area.areaInHectares, 0),
        );

        areas.forEach((area) => {
          // Verificar diferentes estruturas de dados
          let coordinates = null;

          if (area.coordinates && area.coordinates.length > 0) {
            coordinates = area.coordinates;
          } else if (area.geometry && area.geometry.coordinates) {
            coordinates = area.geometry.coordinates;
          } else {
            return;
          }

          if (coordinates && coordinates[0] && coordinates[0].length > 0) {
            const polygonColor = area.color || "#FF0000";

            const polygon = new window.google.maps.Polygon({
              id: area.id,
              paths: coordinates[0].map(([lng, lat]) => ({
                lat: lat,
                lng: lng,
              })),
              fillColor: polygonColor,
              fillOpacity: 0.6, // Aumentado para melhor visibilidade
              strokeColor: polygonColor,
              strokeOpacity: 1,
              strokeWeight: 3, // Aumentado para melhor visibilidade
              clickable: true,
              editable: false,
              draggable: false,
            });

            // Adicionar listener de clique para mostrar informações
            window.google.maps.event.addListener(polygon, "click", () => {
              if (handlePolygonClickRef.current) {
                handlePolygonClickRef.current(area);
              }
            });

            polygon.setMap(mapInstanceRef.current);
            // Garantir que o ID seja string para consistência
            newPolygonsMap.set(String(area.id), polygon);

            // Verificar se a área tem picketId e picketLotId para adicionar ícone visual
            if (area.picketId) {
              // Calcular o centro do polígono usando turf.js
              try {
                const polygonCoords = coordinates[0].map(([lng, lat]) => [
                  lng,
                  lat,
                ]);
                // Fechar o polígono se necessário
                if (polygonCoords.length > 0) {
                  const firstPoint = polygonCoords[0];
                  const lastPoint = polygonCoords[polygonCoords.length - 1];
                  if (
                    firstPoint[0] !== lastPoint[0] ||
                    firstPoint[1] !== lastPoint[1]
                  ) {
                    polygonCoords.push(firstPoint);
                  }
                }

                const turfPolygon = turf.polygon([polygonCoords]);
                const center = turf.centroid(turfPolygon);
                // Certificar que o centro está dentro do polígono
                let [centerLng, centerLat] = center.geometry.coordinates;
                const isInside = turf.inside(
                  turf.point([centerLng, centerLat]),
                  turfPolygon,
                );

                if (!isInside) {
                  // Encontrar o ponto mais próximo dentro do polígono
                  // Usar o ponto do polígono mais próximo ao centro calculado
                  let minDist = Infinity;
                  let closestPoint = polygonCoords[0];
                  for (let i = 0; i < polygonCoords.length; i++) {
                    const pt = polygonCoords[i];
                    const dist = turf.distance(center, turf.point(pt));
                    if (dist < minDist) {
                      minDist = dist;
                      closestPoint = pt;
                    }
                  }
                  [centerLng, centerLat] = closestPoint;
                }

                const iconUrl = ["Break", "B"].includes(area.picketStatus)
                  ? BREAK_ICON
                  : ["Active", "Pasturage", "P"].includes(area.picketStatus)
                    ? BULL_ICON
                    : ["Renovation", "R"].includes(area.picketStatus)
                      ? RENOVATION_ICON
                      : ["Maintenance", "M"].includes(area.picketStatus)
                        ? MAINTENANCE_ICON
                        : null;

                // Criar ícone visual no centro do polígono (não clicável)
                // Usa AdvancedMarkerElement (o google.maps.Marker foi
                // depreciado). O conteúdo é um nó DOM <img> com o SVG.
                const iconImg = document.createElement("img");
                iconImg.src =
                  "data:image/svg+xml;charset=UTF-8," +
                  encodeURIComponent(iconUrl);
                iconImg.style.width = "64px";
                iconImg.style.height = "64px";
                // Centraliza o ícone sobre o centroide (a âncora padrão do
                // AdvancedMarkerElement é a base central do conteúdo).
                iconImg.style.transform = "translateY(50%)";

                const marker =
                  new window.google.maps.marker.AdvancedMarkerElement({
                    position: { lat: centerLat, lng: centerLng },
                    map: mapInstanceRef.current,
                    content: iconImg,
                    gmpClickable: false, // Não clicável - apenas visual
                  });

                // Armazenar referência do ícone junto com o polígono
                newPolygonsMap.set(String(area.id) + "_icon", marker);
              } catch (iconError) {
                logEventError(
                  `Erro ao criar ícone para área ${area.id}:`,
                  iconError,
                );
              }
            }
          }
        });

        setExistingPolygons(newPolygonsMap);
      }
    } catch (error) {
      logEventError("Erro ao carregar áreas existentes:", error);
      notification.error({
        message: translation.areaMap.messages.areasLoadError,
        description: translation.areaMap.messages.areasLoadErrorDescription,
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, translation, clearExistingPolygons]);

  const reloadMap = useCallback(async () => {
    try {
      logEventInfo("Recarregando mapa...");
      // Mostrar loading durante o reload
      // Limpar todos os polígonos
      setDrawnPolygon(null);

      // Recarregar áreas existentes
      loadExistingAreas();

      // Resetar estado do formulário
      setFormValues(initialValues);
      setArea(0);

      // Garantir que o desenho está desabilitado
      disableDrawing();
    } catch (error) {
      logEventError("Erro ao recarregar mapa:", error);
    } finally {
      // Ocultar loading
    }
  }, [loadExistingAreas, disableDrawing]);

  const handleLocationChange = useCallback(
    (locationData) => {
      if (locationData) {
        let newCenter;

        if (locationData.locationType === "address" && locationData.address) {
          // Para endereços, usar as coordenadas obtidas do geocoding
          if (locationData.latitude && locationData.longitude) {
            newCenter = {
              lat: locationData.latitude,
              lng: locationData.longitude,
            };
          } else {
            // Fallback para centro padrão se não houver coordenadas
            newCenter = MAP_CONFIG.DEFAULT_CENTER;
          }
        } else if (
          locationData.locationType === "coordinates" &&
          locationData.latitude &&
          locationData.longitude
        ) {
          newCenter = {
            lat: locationData.latitude,
            lng: locationData.longitude,
          };
        }

        if (newCenter) {
          setMapCenter(newCenter);

          // Aplicar o novo centro ao mapa se já estiver inicializado
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(newCenter);
          }

          // Recarregar o mapa para aplicar a nova centralização
          reloadMap();
        }
      }
    },
    [reloadMap],
  );

  const initializeMap = useCallback(async () => {
    try {
      logEventInfo("Inicializando mapa...");
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        version: "weekly",
        libraries: ["drawing", "geometry", "places"],
      });
      const { Map } = await loader.importLibrary("maps");
      // Biblioteca de marcadores avançados (AdvancedMarkerElement) que
      // substitui o google.maps.Marker (depreciado).
      await loader.importLibrary("marker");

      if (mapRef.current) {
        const map = new Map(mapRef.current, {
          center: mapCenter,
          zoom: MAP_CONFIG.DEFAULT_ZOOM,
          mapTypeId: MAP_CONFIG.MAP_TYPE,
          // mapId é exigido pelo AdvancedMarkerElement para renderizar. Se a
          // env não estiver preenchida, o mapa carrega normalmente (sem os
          // marcadores avançados) em vez de receber um mapId vazio inválido.
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || undefined,
          // O duplo-clique conclui o desenho do polígono; sem isso o mapa
          // daria zoom no duplo-clique.
          disableDoubleClickZoom: true,
        });

        mapInstanceRef.current = map;

        setIsLoading(false);
      }
    } catch (error) {
      logEventError("Erro ao inicializar o mapa:", error);
      notification.error({
        message: translation.areaMap.messages.mapLoadError,
        description: translation.areaMap.messages.mapLoadErrorDescription,
      });
      setIsLoading(false);
    }
  }, [translation, mapCenter]);

  const calculateArea = useCallback((polygon) => {
    if (!polygon) return;

    const path = polygon.getPath();
    const coordinates = [];

    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push([point.lng(), point.lat()]);
    }

    // Fechar o polígono se necessário
    if (coordinates.length > 0) {
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        coordinates.push(firstPoint);
      }
    }

    try {
      const polygonTurf = turf.polygon([coordinates]);
      const areaInSquareMeters = turf.area(polygonTurf);
      const areaInHectares = areaInSquareMeters / 10000; // Converter para hectares

      setArea(parseFloat(areaInHectares.toFixed(2)));
    } catch (error) {
      logEventError("Erro ao calcular área:", error);
    }
  }, []);

  const addPolygonEditListeners = useCallback(
    (polygon) => {
      if (!polygon) {
        return;
      }

      // Limpar listeners anteriores se existirem
      window.google.maps.event.clearListeners(polygon, "dragend");

      const path = polygon.getPath();
      if (path) {
        // Limpar listeners anteriores do path
        window.google.maps.event.clearListeners(path, "insert_at");
        window.google.maps.event.clearListeners(path, "remove_at");
        window.google.maps.event.clearListeners(path, "set_at");

        // Listener para quando um ponto é inserido
        window.google.maps.event.addListener(path, "insert_at", () => {
          calculateArea(polygon);
        });

        // Listener para quando um ponto é removido
        window.google.maps.event.addListener(path, "remove_at", () => {
          calculateArea(polygon);
        });

        // Listener para quando um ponto é movido
        window.google.maps.event.addListener(path, "set_at", () => {
          calculateArea(polygon);
        });
      }

      // Listener para quando o polígono é movido (se habilitado)
      window.google.maps.event.addListener(polygon, "dragend", () => {
        calculateArea(polygon);
      });

      // Forçar a habilitação da edição de forma mais agressiva
      const forceEditable = () => {
        polygon.setOptions({
          editable: true,
          draggable: false,
          clickable: true,
        });

        // Verificar se funcionou
        const isEditable = polygon.getEditable();

        if (!isEditable) {
          // Tentar remover e readicionar o polígono
          const currentPaths = polygon.getPath();
          const pathsArray = [];
          for (let i = 0; i < currentPaths.getLength(); i++) {
            pathsArray.push(currentPaths.getAt(i));
          }

          polygon.setMap(null);
          setTimeout(() => {
            polygon.setPaths(pathsArray);
            polygon.setMap(mapInstanceRef.current);
            polygon.setOptions({
              editable: true,
              draggable: false,
              clickable: true,
            });
          }, 100);
        }
      };

      // Executar imediatamente
      forceEditable();

      // Executar novamente após um delay
      setTimeout(forceEditable, 100);
      setTimeout(forceEditable, 500);
    },
    [calculateArea, mapInstanceRef],
  );

  const handlePolygonClick = useCallback(
    (areaData) => {
      // Durante o desenho de um novo polígono, ignorar cliques em áreas
      // existentes para não abrir o modal de informações.
      if (isDrawingRef.current) return;
      setSelectedAreaData(areaData);
      setModalVisible(true);
    },
    [setSelectedAreaData, setModalVisible],
  );

  // Atualizar a referência sempre que a função mudar
  handlePolygonClickRef.current = handlePolygonClick;

  const disablePolygonEditing = useCallback((polygon) => {
    if (!polygon) return;

    // Remover todos os listeners de edição do polígono
    window.google.maps.event.clearListeners(polygon, "drag");
    window.google.maps.event.clearListeners(polygon, "dragend");
    window.google.maps.event.clearListeners(polygon, "dragstart");

    // Remover listeners do path (vértices) para evitar edição
    const path = polygon.getPath();
    if (path) {
      window.google.maps.event.clearListeners(path, "insert_at");
      window.google.maps.event.clearListeners(path, "remove_at");
      window.google.maps.event.clearListeners(path, "set_at");
    }

    // Tornar o polígono completamente não editável mas clicável para mostrar informações
    polygon.setOptions({
      editable: false,
      draggable: false,
      clickable: true,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setSelectedAreaData(null);
  }, [setModalVisible, setSelectedAreaData]);

  const handleColorChange = useCallback(
    (color) => {
      // Atualizar a referência da cor atual
      currentColorRef.current = color;

      // Atualizar cor do polígono existente se houver
      if (drawnPolygon) {
        drawnPolygon.setOptions({
          fillColor: color,
          strokeColor: color,
        });
      }

      // Se houver um desenho em andamento, atualizar a cor da pré-visualização
      if (tempPolylineRef.current) {
        tempPolylineRef.current.setOptions({ strokeColor: color });
      }
    },
    [drawnPolygon],
  );

  const handleNewArea = useCallback(() => {
    // Se já existe um polígono desenhado, removê-lo
    if (drawnPolygon) {
      drawnPolygon.setMap(null);
      setDrawnPolygon(null);
    }

    setIsFormVisible(true);
    setFormValues(initialValues);
    // Atualizar a referência da cor para a cor padrão
    currentColorRef.current = initialValues.color;
    // Habilitar desenho quando o formulário for aberto com a cor padrão
    setTimeout(() => {
      enableDrawing(initialValues.color);
    }, 100);
  }, [enableDrawing, drawnPolygon]);

  const handleRedrawPolygon = useCallback(() => {
    // Remover polígono atual
    if (drawnPolygon) {
      drawnPolygon.setMap(null);
      setDrawnPolygon(null);
    }

    // Resetar área no formulário
    setArea(0);

    // Garantir que a cor atual seja aplicada ao redesenho
    const currentColor =
      currentColorRef.current || formValues.color || initialValues.color;

    // Habilitar desenho novamente com a cor atual
    setTimeout(() => {
      enableDrawing(currentColor);
    }, 100);
  }, [drawnPolygon, enableDrawing, formValues.color]);

  const handleFormSubmit = useCallback(
    async (values, actions) => {
      try {
        if (!isEditMode && !drawnPolygon) {
          notification.warning({
            message: translation.areaMap.messages.polygonRequired,
            description:
              translation.areaMap.messages.polygonRequiredDescription,
          });
          return;
        }

        const path = drawnPolygon.getPath();
        const coordinates = [];

        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coordinates.push({
            lat: point.lat(),
            lng: point.lng(),
          });
        }

        const mapData = {
          ...values,
          areaInHectares: area,
          areaInSquareMeters: area * 10000,
        };

        // Garantir que o polígono esteja fechado
        if (
          coordinates.length > 0 &&
          (coordinates[0].lat !== coordinates[coordinates.length - 1].lat ||
            coordinates[0].lng !== coordinates[coordinates.length - 1].lng)
        ) {
          coordinates.push(coordinates[0]); // fecha o polígono
        }

        const geoJson = {
          type: "Polygon",
          coordinates: [coordinates.map((coord) => [coord.lng, coord.lat])], // GeoJSON usa [lng, lat]
        };

        mapData.geometry = geoJson;

        // Se estiver em modo de edição, incluir o ID da área
        if (isEditMode && editingAreaData) {
          mapData.id = editingAreaData.id;
        }

        let response = null;
        if (isEditMode) {
          response = await updateFarmMap({
            groupId,
            farmId,
            id: editingAreaData.id,
            mapData,
          });
        } else {
          response = await saveFarmMap({ groupId, farmId, mapData });
          mapData.id = response.data.results.id;
        }

        // Adicionar listener de clique para mostrar informações APÓS desabilitar edição
        window.google.maps.event.addListener(drawnPolygon, "click", () => {
          if (handlePolygonClickRef.current) {
            handlePolygonClickRef.current(mapData);
          }
        });

        const successMessage = isEditMode
          ? translation.areaMap.messages.areaUpdated ||
            "Área atualizada com sucesso!"
          : translation.areaMap.messages.areaSaved;

        const successDescription = isEditMode
          ? translation.areaMap.messages.areaUpdatedDescription?.replace(
              "{area}",
              area,
            ) || `Área de ${area} hectares atualizada com sucesso!`
          : translation.areaMap.messages.areaSavedDescription.replace(
              "{area}",
              area,
            );

        notification.success({
          message: successMessage,
          description: successDescription,
        });

        // Desabilitar completamente a edição do polígono após salvar
        disablePolygonEditing(drawnPolygon);

        // Adicionar o polígono recém-criado ao estado existingPolygons
        if (mapData.id) {
          setExistingPolygons((prev) => {
            const newMap = new Map(prev);
            newMap.set(String(mapData.id), drawnPolygon);
            return newMap;
          });
        }

        // Limpar formulário mas manter polígono no mapa
        setDrawnPolygon(null);
        setFormValues(initialValues);
        setArea(0);
        actions.resetForm();

        // Desabilitar desenho após salvar
        disableDrawing();
        setIsFormVisible(false);

        // Limpar modo de edição
        setIsEditMode(false);
        setEditingAreaData(null);

        loadExistingAreas();
      } catch (error) {
        logEventError("Erro ao salvar área:", error);

        // Verificar se o erro tem código 5042 (piquete/retiro já selecionado)
        if (error?.response?.data?.code === 5042) {
          notification.error({
            message: translation.areaMap.messages.areaSaveError,
            description: translation.areaMap.messages.picketAlreadySelected,
          });
        } else {
          notification.error({
            message: translation.areaMap.messages.areaSaveError,
            description: translation.areaMap.messages.areaSaveErrorDescription,
          });
        }
      }
    },
    [
      isEditMode,
      drawnPolygon,
      area,
      editingAreaData,
      translation.areaMap.messages.areaUpdated,
      translation.areaMap.messages.areaSaved,
      translation.areaMap.messages.areaUpdatedDescription,
      translation.areaMap.messages.areaSavedDescription,
      translation.areaMap.messages.polygonRequired,
      translation.areaMap.messages.polygonRequiredDescription,
      translation.areaMap.messages.areaSaveError,
      translation.areaMap.messages.picketAlreadySelected,
      translation.areaMap.messages.areaSaveErrorDescription,
      disablePolygonEditing,
      disableDrawing,
      setIsEditMode,
      setEditingAreaData,
      loadExistingAreas,
      groupId,
      farmId,
    ],
  );

  const handleCancel = useCallback(() => {
    if (drawnPolygon) {
      // Só remove se não foi salvo
      drawnPolygon.setMap(null);
      setDrawnPolygon(null);
    }

    setIsFormVisible(false);
    setFormValues(initialValues);
    setArea(0);
    // Desabilitar desenho quando o formulário for fechado
    disableDrawing();

    // Limpar modo de edição
    setIsEditMode(false);
    setEditingAreaData(null);
  }, [drawnPolygon, disableDrawing, setIsEditMode, setEditingAreaData]);

  const handleDeleteArea = useCallback(
    async (areaId) => {
      try {
        await deleteFarmMap({ groupId, farmId, id: areaId });

        // Remover o polígono e ícone específicos do mapa imediatamente
        // Garantir que o ID seja string para comparação
        const stringAreaId = String(areaId);
        const polygonToRemove = existingPolygons.get(stringAreaId);
        const iconToRemove = existingPolygons.get(stringAreaId + "_icon");

        if (polygonToRemove) {
          // Verificar se o polígono está realmente no mapa antes de remover
          const currentMap = polygonToRemove.getMap();

          // Limpar todos os listeners do polígono primeiro
          window.google.maps.event.clearListeners(polygonToRemove);

          // Remover o polígono do mapa com múltiplas abordagens
          polygonToRemove.setMap(null);

          // Abordagem alternativa: definir como invisível primeiro
          polygonToRemove.setOptions({
            visible: false,
            fillOpacity: 0,
            strokeOpacity: 0,
          });

          // Depois remover do mapa
          polygonToRemove.setMap(null);

          // Verificar se foi removido
          const mapAfterRemoval = polygonToRemove.getMap();

          // Forçar atualização visual do mapa
          if (mapInstanceRef.current) {
            // Forçar redraw do mapa
            const currentZoom = mapInstanceRef.current.getZoom();
            const currentCenter = mapInstanceRef.current.getCenter();

            // Pequena mudança no zoom para forçar redraw
            mapInstanceRef.current.setZoom(currentZoom + 0.001);
            setTimeout(() => {
              mapInstanceRef.current.setZoom(currentZoom);
              mapInstanceRef.current.setCenter(currentCenter);
            }, 100);
          }

          // Forçar a remoção visual do polígono
          if (mapAfterRemoval !== null) {
            // Tentar remover novamente
            polygonToRemove.setMap(null);
            // Se ainda não funcionou, tentar definir como invisível
            if (polygonToRemove.getMap() !== null) {
              polygonToRemove.setOptions({
                visible: false,
                fillOpacity: 0,
                strokeOpacity: 0,
              });
            }
          }

          // Atualizar o estado removendo o polígono da Map
          setExistingPolygons((prev) => {
            const newMap = new Map(prev);
            newMap.delete(stringAreaId);
            return newMap;
          });
        }

        // Remover o ícone se existir
        if (iconToRemove) {
          // AdvancedMarkerElement é removido do mapa via propriedade map.
          iconToRemove.map = null;

          // Atualizar o estado removendo o ícone da Map
          setExistingPolygons((prev) => {
            const newMap = new Map(prev);
            newMap.delete(stringAreaId + "_icon");
            return newMap;
          });
        }

        notification.success({
          message: translation.areaMap.messages.areaDeleted,
        });

        // Forçar atualização visual do mapa após um pequeno delay
        setTimeout(() => {
          if (mapInstanceRef.current) {
            // Forçar redraw completo do mapa
            const currentZoom = mapInstanceRef.current.getZoom();
            const currentCenter = mapInstanceRef.current.getCenter();

            // Mudança temporária no zoom para forçar redraw completo
            mapInstanceRef.current.setZoom(currentZoom + 0.01);
            setTimeout(() => {
              mapInstanceRef.current.setZoom(currentZoom);
              mapInstanceRef.current.setCenter(currentCenter);
            }, 50);
          }
        }, 200);
      } catch (error) {
        logEventError("Erro ao deletar área:", error);
        notification.error({
          message: translation.areaMap.messages.areaDeleteError,
          description: translation.areaMap.messages.areaDeleteErrorDescription,
        });
      }
    },
    [groupId, farmId, existingPolygons, translation],
  );

  const loadPolygonForEditing = useCallback(
    (areaData) => {
      if (
        !mapInstanceRef.current ||
        !areaData.geometry ||
        !areaData.geometry.coordinates
      ) {
        return;
      }

      // Remover polígono anterior se existir
      if (drawnPolygon) {
        drawnPolygon.setMap(null);
        setDrawnPolygon(null);
      }

      // Não remover polígonos existentes - apenas criar o polígono de edição
      // Os polígonos existentes permanecerão visíveis no mapa

      // Criar novo polígono com as coordenadas da área
      const coordinates = areaData.geometry.coordinates[0].map(
        ([lng, lat]) => ({
          lat: lat,
          lng: lng,
        }),
      );

      // Criar polígono com configuração específica para edição (sem edição de vértices)
      const polygon = new window.google.maps.Polygon({
        paths: coordinates,
        fillColor: areaData.color || "#FF0000",
        fillOpacity: 0.5, // Maior opacidade para destacar que está em edição
        strokeColor: areaData.color || "#FF0000",
        strokeOpacity: 1,
        strokeWeight: 3, // Linha mais grossa para destacar
        clickable: true,
        editable: false, // Desabilitar edição de vértices
        draggable: false,
      });

      polygon.setMap(mapInstanceRef.current);
      setDrawnPolygon(polygon);

      // Desabilitar desenho de novos polígonos
      disableDrawing();

      // Polígono configurado para edição sem modificação de vértices
      // Não adicionar listeners de edição de vértices
    },
    [drawnPolygon, disableDrawing],
  );

  const handleEditArea = useCallback(
    (areaData) => {
      // Fechar o modal de informações
      setModalVisible(false);
      setSelectedAreaData(null);

      // Configurar modo de edição
      setIsEditMode(true);
      setEditingAreaData(areaData);

      // Preencher o formulário com os dados da área
      const editValues = {
        identificationCode: areaData.identificationCode || "",
        type: areaData.type || "",
        picketId: areaData.picketId || null,
        retreatId: areaData.retreatId || null,
        observation: areaData.observation || "",
        color: areaData.color || "#FF0000",
        area: areaData.areaInHectares || 0,
        areaInSquareMeters: areaData.areaInSquareMeters || 0,
        areaInHectares: areaData.areaInHectares || 0,
      };

      setFormValues(editValues);
      setArea(areaData.areaInHectares || 0);
      // Atualizar a referência da cor para a cor da área sendo editada
      currentColorRef.current = editValues.color;

      // Mostrar o formulário
      setIsFormVisible(true);

      // Aguardar um pouco para garantir que o mapa esteja pronto
      // Verificar diferentes possíveis estruturas de dados de geometria
      let geometryData = null;

      if (areaData.geometry && areaData.geometry.coordinates) {
        geometryData = areaData.geometry;
      } else if (areaData.coordinates) {
        // Talvez os dados estejam em areaData.coordinates diretamente
        geometryData = {
          type: "Polygon",
          coordinates: areaData.coordinates,
        };
      } else {
        return;
      }

      if (
        geometryData &&
        geometryData.coordinates &&
        geometryData.coordinates[0]
      ) {
        loadPolygonForEditing({ ...areaData, geometry: geometryData });
      }
    },
    [
      setModalVisible,
      setSelectedAreaData,
      setIsEditMode,
      setEditingAreaData,
      loadPolygonForEditing,
    ],
  );

  // Manter a referência dos polígonos existentes em dia para que o desenho
  // customizado possa alternar a clicabilidade deles sem closures defasadas.
  useEffect(() => {
    existingPolygonsRef.current = existingPolygons;
  }, [existingPolygons]);

  useEffect(() => {
    if (groupId && farmId) {
      initializeMap();
      fetchActivities();
    }
  }, [groupId, farmId, initializeMap, fetchActivities]);

  useEffect(() => {
    if (groupId && farmId) {
      // loadExistingAreas();
    }
  }, [groupId, farmId, loadExistingAreas]);

  return (
    <>
      {shouldShowDashboardsExtensive && (
        <Row type="flex">
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <PastureStatusDashboard />
          </Col>
        </Row>
      )}
      <MapCenterCard
        onLocationChange={handleLocationChange}
        translation={translation}
        savedLocation={savedLocation}
        setSavedLocation={setSavedLocation}
      />
      <Row type="flex" justify="end" style={{ marginBottom: "16px" }}>
        <Col>
          <ButtonStandard
            buttonType="typeWithoutBackground"
            onClick={() => loadExistingAreas()}
          >
            <Icon type="reload" />
          </ButtonStandard>
        </Col>
        <Col>
          <ButtonStandard
            buttonType="addNew"
            onClick={handleNewArea}
            disabled={isLoading}
          >
            {translation.areaMap.newArea}
          </ButtonStandard>
        </Col>
      </Row>
      <Spin spinning={isLoading}>
        <Row type="flex" justify="start" gutter={[8, 8]}>
          <Col>
            <Typography.Title level={4}>
              {translation.areaMap.totalAreas || "Total"}:{" "}
              {numberMask(totalAreas || 0, false)} ha
            </Typography.Title>
          </Col>
        </Row>
        <Row type="flex" gutter={16} style={{ height: "100%" }}>
          <Col
            xs={24}
            lg={isFormVisible ? 16 : 24}
            xl={isFormVisible ? 16 : 24}
            xxl={isFormVisible ? 16 : 24}
          >
            <Card>
              <MapDrawWrapper>
                <MapContainer ref={mapRef} />
                {isDrawingActive && (
                  <DrawingOverlay>
                    <strong>{translation.areaMap.messages.drawingHint}</strong>
                    <span>
                      {translation.areaMap.messages.drawingHintDescription}
                    </span>
                    <div className="meta">
                      <span>
                        {translation.areaMap.messages.drawingVerticesLabel}:{" "}
                        {vertexCount}
                      </span>
                      <button
                        type="button"
                        onClick={finishDrawing}
                        disabled={vertexCount < 3}
                      >
                        {translation.areaMap.messages.drawingFinishButton}
                      </button>
                    </div>
                  </DrawingOverlay>
                )}
              </MapDrawWrapper>
            </Card>
          </Col>
          {isFormVisible && (
            <Col xs={24} lg={8}>
              <FormCard>
                <Formik
                  initialValues={formValues}
                  validationSchema={getValidationSchema(translation)}
                  onSubmit={handleFormSubmit}
                  enableReinitialize
                >
                  {(formikProps) => (
                    <AreaForm
                      {...formikProps}
                      area={area}
                      translation={translation}
                      onCancel={handleCancel}
                      onColorChange={handleColorChange}
                      onRedrawPolygon={handleRedrawPolygon}
                    />
                  )}
                </Formik>
              </FormCard>
            </Col>
          )}
        </Row>
      </Spin>
      <AreaInfoModal
        onClose={closeModal}
        onEditArea={handleEditArea}
        onDeleteArea={handleDeleteArea}
        translation={translation}
      />
    </>
  );
};

const AreaMapDashboard = () => {
  return (
    <AreaMapProvider>
      <AreaMapDashboardLayout />
    </AreaMapProvider>
  );
};

export default AreaMapDashboard;
